import { type FixedDepartureDayOut, type FixedDepartureDetail } from '@utazzo/shared';
import { UzButton, UzEmptyState, UzInput, UzSelect, UzTextarea } from '@utazzo/ui';
import { CalendarDays } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { useUpdateFixedDepartureDay } from '../use-fixed-departures';

import { PhotoGalleryField, type GalleryPhoto } from '@/components/photo-gallery';
import { useMealPlansList } from '@/features/meal-plans/use-meal-plans';

interface DayDraft {
  title: string;
  subtitle: string;
  mealPlanId: string;
  briefHtml: string;
  /**
   * Cover + gallery, in PhotoGalleryField's shape. photos[0] (sortOrder 0) is
   * the cover; the rest are the gallery. Stored as media IDs.
   */
  photos: GalleryPhoto[];
}

const NO_MEAL = '__none__';

/** DB fields (hero id + gallery ids) -> PhotoGalleryField photos. */
const toPhotos = (heroId: string | null, galleryIds: string[]): GalleryPhoto[] => [
  ...(heroId ? [{ mediaId: heroId, sortOrder: 0 }] : []),
  ...galleryIds.map((mediaId, i) => ({ mediaId, sortOrder: i + 1 })),
];

/** photos -> the hero id + gallery ids the API expects. */
const fromPhotos = (
  photos: GalleryPhoto[],
): { heroImageUrl: string | null; galleryImageUrls: string[] } => {
  const sorted = [...photos].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const ids = sorted.map((p) => p.mediaId).filter((id): id is string => Boolean(id));
  return { heroImageUrl: ids[0] ?? null, galleryImageUrls: ids.slice(1) };
};

const toDraft = (day: FixedDepartureDayOut): DayDraft => ({
  title: day.title ?? '',
  subtitle: day.subtitle ?? '',
  mealPlanId: day.mealPlanId ?? NO_MEAL,
  briefHtml: day.briefHtml ?? '',
  photos: toPhotos(day.heroImageUrl, day.galleryImageUrls),
});

const trimOrNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

const isDirty = (draft: DayDraft, day: FixedDepartureDayOut): boolean => {
  const p = fromPhotos(draft.photos);
  return (
    trimOrNull(draft.title) !== day.title ||
    trimOrNull(draft.subtitle) !== day.subtitle ||
    (draft.mealPlanId === NO_MEAL ? null : draft.mealPlanId) !== day.mealPlanId ||
    trimOrNull(draft.briefHtml) !== day.briefHtml ||
    p.heroImageUrl !== day.heroImageUrl ||
    p.galleryImageUrls.join('|') !== day.galleryImageUrls.join('|')
  );
};

/**
 * Days step: one card per generated day slot. Everything is written by hand -
 * there is no auto-fill from a master or a supplier here, by design.
 *
 * Day rows are created by the Configure step (cities x nights + 1), so this
 * screen only ever edits content; it can neither add nor remove a day.
 */
export const FdDaysStep = ({ fd }: { fd: FixedDepartureDetail }): React.JSX.Element => {
  const updateDay = useUpdateFixedDepartureDay(fd.id);
  const mealPlans = useMealPlansList({ page: 1, pageSize: 100, sortDir: 'asc', isActive: true });

  const [drafts, setDrafts] = useState<Record<string, DayDraft>>(() =>
    Object.fromEntries(fd.days.map((d) => [d.id, toDraft(d)])),
  );
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    setDrafts(Object.fromEntries(fd.days.map((d) => [d.id, toDraft(d)])));
  }, [fd.days]);

  const mealOptions = [
    { value: NO_MEAL, label: 'No meal' },
    ...(mealPlans.data?.data ?? []).map((m) => ({ value: m.id, label: m.name })),
  ];

  const setDraft = (dayId: string, patch: Partial<DayDraft>): void =>
    setDrafts((prev) => {
      const current = prev[dayId];
      if (!current) return prev;
      return { ...prev, [dayId]: { ...current, ...patch } };
    });

  const save = async (day: FixedDepartureDayOut): Promise<void> => {
    const draft = drafts[day.id];
    if (!draft) return;
    setSavingId(day.id);
    try {
      await updateDay.mutateAsync({
        dayId: day.id,
        payload: {
          title: trimOrNull(draft.title),
          subtitle: trimOrNull(draft.subtitle),
          mealPlanId: draft.mealPlanId === NO_MEAL ? null : draft.mealPlanId,
          briefHtml: trimOrNull(draft.briefHtml),
          ...fromPhotos(draft.photos),
        },
      });
    } finally {
      setSavingId(null);
    }
  };

  if (fd.days.length === 0) {
    return (
      <UzEmptyState
        icon={<CalendarDays className="h-6 w-6" aria-hidden />}
        title="No days yet"
        description="Add the cities and nights on the Configure step first - the day slots are generated from them."
      />
    );
  }

  return (
    <div className="space-y-4">
      {fd.days.map((day) => {
        const draft = drafts[day.id];
        if (!draft) return null;
        const dirty = isDirty(draft, day);
        return (
          <section key={day.id} className="rounded-lg border border-zinc-200 bg-white">
            <header className="flex items-center justify-between gap-3 border-b border-zinc-100 px-5 py-3">
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-semibold text-zinc-900">Day {day.dayIndex}</span>
                {day.cityName && <span className="text-xs text-zinc-500">{day.cityName}</span>}
              </div>
              <UzButton
                size="sm"
                variant={dirty ? 'default' : 'ghost'}
                disabled={!dirty}
                loading={savingId === day.id}
                onClick={() => save(day)}
              >
                {dirty ? 'Save day' : 'Saved'}
              </UzButton>
            </header>
            <div className="space-y-4 p-5">
              <div className="grid gap-4 sm:grid-cols-[1fr_1fr_180px]">
                <UzInput
                  label="Title"
                  placeholder="e.g. Arrive Srinagar"
                  value={draft.title}
                  onChange={(e) => setDraft(day.id, { title: e.target.value })}
                />
                <UzInput
                  label="Subtitle"
                  placeholder="e.g. Shikara ride on Dal Lake"
                  value={draft.subtitle}
                  onChange={(e) => setDraft(day.id, { subtitle: e.target.value })}
                />
                <UzSelect
                  label="Meal"
                  value={draft.mealPlanId}
                  onValueChange={(v) => setDraft(day.id, { mealPlanId: v })}
                  options={mealOptions}
                />
              </div>
              <UzTextarea
                label="Day brief"
                rows={5}
                placeholder="What the traveller does on this day."
                value={draft.briefHtml}
                onChange={(e) => setDraft(day.id, { briefHtml: e.target.value })}
              />

              {/* Cover + gallery, uploaded to S3. The cover leads the day on
                  the docket / PDF; both are copied onto the itinerary's day when
                  one is created from this template. */}
              <PhotoGalleryField
                photos={draft.photos}
                onChange={(photos) => setDraft(day.id, { photos })}
                folder="fixed-departures/days"
                coverLabel="Cover image"
              />
            </div>
          </section>
        );
      })}
    </div>
  );
};
